import { Check, FileSpreadsheet, MessageCircle, Building2, Receipt, CalendarClock, Loader2, X, MessageCircleMore } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Pre-send workflow stepper (vertical) — 5 connected verification stages.
 * Each stage has its own brand colour so the visual hierarchy maps directly
 * to the action.  The parent owns the verification state and passes it via
 * the `stages` prop:
 *
 *   stages = [
 *     { status: "idle"|"loading"|"verified"|"error", info?: string, error?: string },
 *     ...
 *   ]
 *
 * `onStepClick(idx)` fires when the user clicks any block.
 *
 * UX: the next-due (idle) stage pulses softly to draw attention.
 * If a stage is in error state, a "פנה לתמיכה בוואטסאפ" CTA appears below
 * its label so the user can reach out for help in one click.
 */
const SUPPORT_WA_NUMBER = "972504944192"; // FlowUp support WhatsApp

const STAGE_CONFIG = [
  {
    label: "טעינת גיליון",
    sub: "Google Sheets",
    icon: FileSpreadsheet,
    testid: "step-sheet",
    palette: {
      verified: "bg-orange-500 text-white ring-orange-100",
      idle: "bg-orange-50 text-orange-600 ring-orange-100",
      loading: "bg-orange-400 text-white ring-orange-100",
      error: "bg-red-500 text-white ring-red-100",
      label: "text-orange-700",
      badge: "bg-orange-50 text-orange-700 border-orange-200",
      connector: "bg-orange-500",
    },
  },
  {
    label: "מספר וואטסאפ מאומת",
    sub: "אישור Brevo + מספר השליחה",
    icon: MessageCircle,
    testid: "step-whatsapp",
    palette: {
      verified: "bg-green-500 text-white ring-green-100",
      idle: "bg-green-50 text-green-600 ring-green-100",
      loading: "bg-green-400 text-white ring-green-100",
      error: "bg-red-500 text-white ring-red-100",
      label: "text-green-700",
      badge: "bg-green-50 text-green-700 border-green-200",
      connector: "bg-green-500",
    },
  },
  {
    label: "חשבון עסק מטא",
    sub: "Meta Business + WABA",
    icon: Building2,
    testid: "step-meta",
    palette: {
      verified: "bg-blue-500 text-white ring-blue-100",
      idle: "bg-blue-50 text-blue-600 ring-blue-100",
      loading: "bg-blue-400 text-white ring-blue-100",
      error: "bg-red-500 text-white ring-red-100",
      label: "text-blue-700",
      badge: "bg-blue-50 text-blue-700 border-blue-200",
      connector: "bg-blue-500",
    },
  },
  {
    label: "חשבונית ירוקה",
    sub: "Green Invoice API",
    icon: Receipt,
    testid: "step-greeninvoice",
    palette: {
      verified: "bg-emerald-400 text-white ring-emerald-100",
      idle: "bg-emerald-50 text-emerald-600 ring-emerald-100",
      loading: "bg-emerald-300 text-white ring-emerald-100",
      error: "bg-red-500 text-white ring-red-100",
      label: "text-emerald-700",
      badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
      connector: "bg-emerald-400",
    },
  },
  {
    label: "תזמון",
    sub: "מתי לשלוח",
    icon: CalendarClock,
    testid: "step-schedule",
    palette: {
      verified: "bg-purple-500 text-white ring-purple-100",
      idle: "bg-purple-50 text-purple-600 ring-purple-100",
      loading: "bg-purple-400 text-white ring-purple-100",
      error: "bg-red-500 text-white ring-red-100",
      label: "text-purple-700",
      badge: "bg-purple-50 text-purple-700 border-purple-200",
      connector: "bg-purple-500",
    },
  },
];

const supportHrefFor = (stageLabel) => {
  const msg = encodeURIComponent(`היי, אני נתקלת בבעיה בשלב "${stageLabel}" במערכת FlowUp ואשמח לעזרה לתיקון.`);
  return `https://wa.me/${SUPPORT_WA_NUMBER}?text=${msg}`;
};

export const Stepper = ({ stages = [], onStepClick }) => {
  // Index of the next-due stage (first idle one). -1 means everything is
  // either verified or in error state. Used to softly pulse the next cube.
  const nextDueIdx = stages.findIndex((s) => (s?.status || "idle") === "idle");

  return (
    <div className="bg-white rounded-2xl border border-border p-4 sm:p-5" data-testid="workflow-stepper">
      <div className="flex flex-col gap-1">
        {STAGE_CONFIG.map((step, idx) => {
          const stage = stages[idx] || { status: "idle" };
          const Icon = step.icon;
          const isLast = idx === STAGE_CONFIG.length - 1;
          const isVerified = stage.status === "verified";
          const isLoading = stage.status === "loading";
          const isError = stage.status === "error";
          const isNextDue = idx === nextDueIdx;
          const p = step.palette;

          const circleClass = isError
            ? p.error
            : isVerified
            ? p.verified
            : isLoading
            ? p.loading
            : p.idle;

          const labelClass = isError
            ? "text-red-600"
            : isVerified || isLoading
            ? p.label
            : isNextDue
            ? p.label
            : "text-stone-500";

          return (
            <div key={idx} className="flex items-stretch gap-3" data-testid={step.testid}>
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => onStepClick && onStepClick(idx)}
                  className={cn(
                    "h-12 w-12 rounded-xl flex items-center justify-center transition-all duration-200 shrink-0 cursor-pointer hover:scale-105 shadow-sm ring-4",
                    circleClass,
                    // Subtle pulse on the next-due stage so the user always
                    // knows what to do next.
                    isNextDue && !isLoading && "animate-pulse-soft",
                    !isVerified && !isLoading && !isError && "hover:shadow"
                  )}
                  data-testid={`${step.testid}-button`}
                  aria-label={step.label}
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : isError ? (
                    <X className="h-5 w-5" />
                  ) : isVerified ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </button>
                {!isLast && (
                  <div className="flex-1 w-0.5 my-2 relative min-h-[64px]">
                    <div className="absolute inset-0 bg-stone-200 rounded-full" />
                    <div
                      className={cn(
                        "absolute inset-0 rounded-full transition-all duration-500",
                        isVerified ? `${p.connector} h-full` : "h-0"
                      )}
                    />
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => onStepClick && onStepClick(idx)}
                className={cn(
                  "flex-1 text-right pb-7 cursor-pointer hover:opacity-80 transition",
                  isLast && "pb-0"
                )}
                data-testid={`${step.testid}-label`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={cn("font-bold text-sm sm:text-base leading-tight", labelClass)}>
                    {step.label}
                  </p>
                  {isVerified && (
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border",
                        p.badge
                      )}
                      data-testid={`${step.testid}-verified-badge`}
                    >
                      <Check className="h-3 w-3" />
                      מאומת
                    </span>
                  )}
                  {isError && (
                    <span
                      className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-200"
                      data-testid={`${step.testid}-error-badge`}
                    >
                      <X className="h-3 w-3" />
                      שגיאה
                    </span>
                  )}
                </div>
                <p className={cn("text-[11px] sm:text-xs mt-1 truncate max-w-[200px]", isVerified || isLoading ? "text-stone-700" : "text-stone-400")}>
                  {stage.info || step.sub}
                </p>
                {isError && stage.error && (
                  <p className="text-[10px] text-red-600 mt-1 max-w-[200px]" title={stage.error}>
                    {stage.error}
                  </p>
                )}

                {/* Support CTA when this stage is broken — opens WhatsApp chat
                    with the FlowUp team so the user can get unstuck in 1 click. */}
                {isError && (
                  <a
                    href={supportHrefFor(step.label)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-2 inline-flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm transition"
                    data-testid={`${step.testid}-support-link`}
                  >
                    <MessageCircleMore className="h-3 w-3" />
                    פנה לתמיכה בוואטסאפ
                  </a>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
